import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Table, message } from "antd";
import MainLayout from "../layouts/layout";
import { useAuth, isAdmin } from "../authContext";
import {
    getAgents,
    resetPassword,
    createAgent,
    deleteAgent,
    grantAdmin,
    revokeAdmin,
} from "../services/adminService";
import styles from "../styles/Admin.module.css";

/**
 * Compara correos como lo hace Cognito: sin distinguir mayúsculas.
 *
 * Se compara normalizado en vez de normalizar la lista, para seguir mostrando
 * el correo tal como está guardado.
 */
const sameEmail = (a, b) =>
    (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

// Cómo se lee cada estado de Cognito para quien administra, no para quien
// programó el user pool.
const STATUS_LABEL = {
    CONFIRMED: { text: 'Activo', tone: 'ok' },
    FORCE_CHANGE_PASSWORD: { text: 'Debe cambiar su contraseña', tone: 'pending' },
    RESET_REQUIRED: { text: 'Necesita restablecer', tone: 'pending' },
    UNCONFIRMED: { text: 'Sin confirmar', tone: 'pending' },
};

export default function Admin() {
    const navigate = useNavigate();
    const { tokens } = useAuth();
    const [messageApi, contextHolder] = message.useMessage();

    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(null);
    // La contraseña generada se muestra una sola vez, al crearla.
    const [newCredential, setNewCredential] = useState(null);

    // Alta
    const [creating, setCreating] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [createError, setCreateError] = useState(null);

    // Baja: hay que reescribir el correo, por ser irreversible.
    const [deleting, setDeleting] = useState(null);
    const [confirmEmail, setConfirmEmail] = useState('');

    // El estado `working` no basta para frenar un doble envío: dos eventos en el
    // mismo tick leen el mismo valor, porque el re-render aún no ocurrió.
    const inFlight = useRef(false);

    const admin = tokens ? isAdmin(tokens) : false;

    // La sesión ya la validó RequireAuth; aquí solo queda comprobar el rol.
    // Aunque el backend lo rechaza igual, no tiene sentido mostrar la pantalla
    // a quien no puede usarla.
    useEffect(() => {
        if (!admin) {
            navigate('/');
        }
    }, [admin, navigate]);

    const loadAgents = useCallback(async () => {
        if (!tokens?.AccessToken) return;
        setLoading(true);
        try {
            setAgents(await getAgents(tokens.AccessToken));
        } catch (error) {
            messageApi.error(
                error.response?.data?.message || 'No se pudo cargar la lista de agentes'
            );
        } finally {
            setLoading(false);
        }
    }, [tokens, messageApi]);

    useEffect(() => { loadAgents(); }, [loadAgents]);

    const myEmail = tokens?.userAttributes?.find((a) => a.Name === 'email')?.Value;

    const handleCreate = async () => {
        if (inFlight.current) return;

        const email = newEmail.trim().toLowerCase();
        if (!email) {
            setCreateError('Escribe el correo del agente');
            return;
        }

        inFlight.current = true;
        setWorking('new');
        setCreateError(null);
        try {
            await createAgent(email, tokens.AccessToken);
            messageApi.success(`Invitación enviada a ${email}`);
            setCreating(false);
            setNewEmail('');
            loadAgents();
        } catch (error) {
            setCreateError(
                error.response?.data?.message || 'No se pudo crear el agente'
            );
        } finally {
            inFlight.current = false;
            setWorking(null);
        }
    };

    const handleDelete = async () => {
        if (inFlight.current) return;
        if (!sameEmail(confirmEmail, deleting.email)) return;

        inFlight.current = true;
        setWorking(deleting.email);
        try {
            await deleteAgent(deleting.email, confirmEmail.trim(), tokens.AccessToken);
            messageApi.success(`${deleting.email} fue eliminado`);
            setDeleting(null);
            setConfirmEmail('');
            loadAgents();
        } catch (error) {
            messageApi.error(
                error.response?.data?.message || 'No se pudo eliminar el agente'
            );
        } finally {
            inFlight.current = false;
            setWorking(null);
        }
    };

    const handleToggleAdmin = (agent) => {
        const granting = !agent.isAdmin;
        Modal.confirm({
            title: granting ? 'Dar permisos de administrador' : 'Quitar permisos de administrador',
            content: granting
                ? `${agent.email} podrá crear y eliminar agentes, restablecer contraseñas y gestionar administradores.`
                : `${agent.email} dejará de ver este panel y de administrar agentes.`,
            okText: granting ? 'Dar permisos' : 'Quitar permisos',
            cancelText: 'Cancelar',
            okButtonProps: { danger: !granting },
            onOk: async () => {
                setWorking(agent.email);
                try {
                    if (granting) {
                        await grantAdmin(agent.email, tokens.AccessToken);
                        messageApi.success(`${agent.email} ahora es administrador`);
                    } else {
                        await revokeAdmin(agent.email, tokens.AccessToken);
                        messageApi.success(`${agent.email} ya no es administrador`);
                    }
                    loadAgents();
                } catch (error) {
                    messageApi.error(
                        error.response?.data?.message || 'No se pudo cambiar el rol'
                    );
                } finally {
                    setWorking(null);
                }
            },
        });
    };

    const handleResetPassword = (agent) => {
        Modal.confirm({
            title: 'Restablecer contraseña',
            content: `Se generará una contraseña temporal para ${agent.email}. La actual dejará de servir y tendrá que elegir una nueva al entrar.`,
            okText: 'Restablecer',
            cancelText: 'Cancelar',
            onOk: async () => {
                setWorking(agent.email);
                try {
                    const result = await resetPassword(agent.email, tokens.AccessToken);
                    setNewCredential(result);
                    loadAgents();
                } catch (error) {
                    messageApi.error(
                        error.response?.data?.message || 'No se pudo restablecer la contraseña'
                    );
                } finally {
                    setWorking(null);
                }
            },
        });
    };

    const columns = [
        {
            title: 'Agente',
            dataIndex: 'email',
            key: 'email',
            render: (email, agent) => (
                <span>
                    {email}
                    {agent.isAdmin && <span className={styles.adminTag}>admin</span>}
                </span>
            ),
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (status, agent) => {
                if (!agent.enabled) {
                    return <span className={styles.statusOff}>Deshabilitado</span>;
                }
                const label = STATUS_LABEL[status] || { text: status, tone: 'pending' };
                return (
                    <span className={label.tone === 'ok' ? styles.statusOk : styles.statusPending}>
                        {label.text}
                    </span>
                );
            },
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, agent) => {
                const isSelf = sameEmail(agent.email, myEmail);
                return (
                    <div className={styles.actions}>
                        <button
                            className={styles.actionBtn}
                            disabled={working === agent.email || isSelf}
                            onClick={() => handleResetPassword(agent)}
                            title={isSelf ? 'No puedes restablecer tu propia contraseña' : ''}
                        >
                            Restablecer contraseña
                        </button>
                        <button
                            className={styles.actionBtnSecondary}
                            disabled={working === agent.email || isSelf}
                            onClick={() => handleToggleAdmin(agent)}
                            title={isSelf ? 'No puedes cambiar tu propio rol' : ''}
                        >
                            {agent.isAdmin ? 'Quitar admin' : 'Hacer admin'}
                        </button>
                        <button
                            className={styles.actionBtnDanger}
                            disabled={working === agent.email || isSelf}
                            onClick={() => { setDeleting(agent); setConfirmEmail(''); }}
                            title={isSelf ? 'No puedes eliminar tu propia cuenta' : ''}
                        >
                            Eliminar
                        </button>
                    </div>
                );
            },
        },
    ];

    if (!admin) return null;

    return (
        <MainLayout>
            {contextHolder}
            <div className={styles.page}>
                <div className={styles.header}>
                    <div>
                        <h1 className={styles.title}>Agentes</h1>
                        <p className={styles.subtitle}>
                            Da de alta agentes, restablece contraseñas y gestiona quién
                            administra la plataforma.
                        </p>
                    </div>
                    <button
                        className={styles.primaryBtn}
                        onClick={() => {
                            setNewEmail('');
                            setCreateError(null);
                            setCreating(true);
                        }}
                    >
                        Agregar agente
                    </button>
                </div>

                <Table
                    columns={columns}
                    dataSource={agents.map((a) => ({ ...a, key: a.email }))}
                    loading={loading}
                    pagination={false}
                    size="middle"
                />
            </div>

            <Modal
                open={!!newCredential}
                onCancel={() => setNewCredential(null)}
                onOk={() => setNewCredential(null)}
                okText="Listo"
                cancelButtonProps={{ style: { display: 'none' } }}
                title="Contraseña temporal"
            >
                {newCredential && (
                    <div>
                        <p className={styles.modalText}>
                            Entrégasela a <strong>{newCredential.email}</strong> por un canal
                            seguro. No queda guardada: al cerrar esta ventana no podrás volver
                            a verla.
                        </p>
                        <code className={styles.credential}>
                            {newCredential.temporaryPassword}
                        </code>
                        <button
                            className={styles.copyBtn}
                            onClick={() => {
                                navigator.clipboard?.writeText(newCredential.temporaryPassword);
                                messageApi.success('Contraseña copiada');
                            }}
                        >
                            Copiar
                        </button>
                        <p className={styles.modalHint}>
                            Al entrar con ella tendrá que elegir una contraseña nueva y
                            verificar su correo con un código.
                        </p>
                    </div>
                )}
            </Modal>
            <Modal
                open={creating}
                destroyOnClose
                title="Agregar agente"
                okText="Enviar invitación"
                cancelText="Cancelar"
                confirmLoading={working === 'new'}
                onOk={handleCreate}
                onCancel={() => { setCreating(false); setNewEmail(''); setCreateError(null); }}
            >
                <p className={styles.modalText}>
                    Recibirá un correo con una contraseña temporal. Al entrar tendrá que
                    elegir una nueva y verificar su correo con un código.
                </p>
                <input
                    className={styles.input}
                    type="email"
                    placeholder="nombre@soymomo.com"
                    value={newEmail}
                    onChange={(e) => { setNewEmail(e.target.value); setCreateError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                    autoFocus
                />
                {createError && <p className={styles.modalError}>{createError}</p>}
            </Modal>

            <Modal
                open={!!deleting}
                title="Eliminar agente"
                okText="Eliminar"
                cancelText="Cancelar"
                okButtonProps={{
                    danger: true,
                    // No hay vuelta atrás: el botón no se activa hasta que el
                    // correo escrito coincide.
                    disabled: !sameEmail(confirmEmail, deleting?.email),
                }}
                confirmLoading={working === deleting?.email}
                onOk={handleDelete}
                onCancel={() => { setDeleting(null); setConfirmEmail(''); }}
            >
                <p className={styles.modalText}>
                    <strong>{deleting?.email}</strong> perderá el acceso de inmediato y no
                    se puede deshacer. Para confirmar, escribe su correo.
                </p>
                <input
                    className={styles.input}
                    type="email"
                    placeholder={deleting?.email}
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    autoFocus
                />
            </Modal>
        </MainLayout>
    );
}
